import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ArrowLeft, Search, PlusCircle, Lock, Users,
  Hash, Radio, Loader2, X, AlertCircle
} from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { formatDistanceToNow } from 'date-fns';
import { 
  collection, query, where, onSnapshot, orderBy, 
  doc, updateDoc, arrayUnion, getDoc 
} from 'firebase/firestore';

const randomNames = [
    'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
    'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
    'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer'
];
const getRandomAlias = () => randomNames[Math.floor(Math.random() * randomNames.length)];

export default function CustomRoomsHub({ isEmbedded = false }) {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [passwordInput, setPasswordInput] = useState('');
    const [needsPassword, setNeedsPassword] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [isJoining, setIsJoining] = useState(false);

    const user = auth.currentUser;

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        const q = query(
            collection(db, 'customVoiceRooms'),
            where('isOpen', '==', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const activeRooms = [];
            snapshot.docs.forEach(doc => {
                activeRooms.push({ id: doc.id, ...doc.data() });
            });
            // Sort client-side to bypass the need for a composite index
            activeRooms.sort((a, b) => {
                const tA = a.createdAt?.toMillis() || 0;
                const tB = b.createdAt?.toMillis() || 0;
                return tB - tA;
            });
            setRooms(activeRooms);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching custom rooms:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user, navigate]);

    const handleRoomClick = async (room) => {
        setJoinError('');
        
        if (room.status === 'Ended') {
            toast.error('This room has ended and is no longer available.');
            return;
        }

        if (room.participantUids?.includes(user.uid)) {
            navigate(`/custom-voice-room/${room.id}`, { state: { roomCode: room.code } });
            return;
        }

        const currentCount = room.participantUids?.length || 0;
        const max = room.maxSize || 8;
        if (currentCount >= max) {
            toast.error(`Room "${room.name}" is full.`);
            return;
        }

        if (room.password) {
            setSelectedRoom(room);
            setNeedsPassword(true);
            return;
        }

        await executeJoin(room.id, room);
    };

    const handlePasswordSubmit = async () => {
        if (!passwordInput.trim()) {
            setJoinError('Please enter the room password.');
            return;
        }
        
        if (passwordInput.trim() !== selectedRoom.password) {
            setJoinError('Incorrect password. Try again.');
            return;
        }
        
        setJoinError('');
        await executeJoin(selectedRoom.id, selectedRoom);
    };

    const executeJoin = async (roomId, roomData) => {
        setIsJoining(true);
        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const uData = userDoc.data() || {};
            const myAlias = getRandomAlias();

            await updateDoc(doc(db, 'customVoiceRooms', roomId), {
                participantUids: arrayUnion(user.uid),
                [`participantInfo.${user.uid}`]: {
                    alias: myAlias,
                    college: uData.college || 'Unknown',
                    voiceEffect: null,
                    muted: false
                }
            });
            
            try { logEvent(analytics, 'custom_room_joined', { join_method: 'hub_list' }); } catch(e) {}
            navigate(`/custom-voice-room/${roomId}`, { state: { roomCode: roomData.code } });
            
        } catch (e) {
            setJoinError('Failed to join room. Please try again.');
            setIsJoining(false);
        }
    };

    const filteredRooms = rooms.filter(r => 
        r.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        r.code?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="w-full sm:w-[700px] min-h-screen pb-36 sm:pb-6 border-r border-gray-800 flex flex-col bg-black text-white shrink-0 relative overflow-hidden font-poppins selection:bg-[#0BD9E5]/30">
            <div className="relative z-10 px-4 py-6 bg-black/50 backdrop-blur-md border-b border-white/10 shrink-0">
                <div className="flex items-center gap-4 mb-6 w-full">
                   {!isEmbedded && (
                       <button onClick={() => navigate('/')} className="p-2 hover:bg-white/10 rounded-full transition-colors shrink-0">
                          <ChevronLeft size={28} className="text-white" />
                       </button>
                   )}
                   <div className="flex items-center justify-between flex-1 min-w-0 pr-2">
                      <h1 className="text-2xl font-black tracking-tight truncate">Voice Rooms</h1>
                   </div>
                </div>

                <div className="flex gap-3 mb-4">
                    <button 
                        onClick={() => navigate('/create-room')}
                        className="flex-1 bg-[#0BD9E5] text-black font-bold py-3 px-4 rounded-xl hover:bg-[#09bcc7] transition-colors flex items-center justify-center gap-2"
                    >
                        <PlusCircle size={18} /> Create
                    </button>
                    <button 
                        onClick={() => navigate('/join-room')}
                        className="flex-1 bg-white/5 border border-white/10 text-white font-bold py-3 px-4 rounded-xl hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                    >
                        <Hash size={18} /> Join Code
                    </button>
                </div>

                <div className="relative flex items-center bg-[#111] border border-gray-800 focus-within:border-[#0BD9E5]/50 rounded-2xl px-4 py-3 transition-colors">
                    <Search size={18} className="text-gray-500 mr-3 shrink-0" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search rooms..."
                        className="bg-transparent border-none text-white w-full focus:outline-none text-sm placeholder:text-gray-600"
                    />
                    {searchQuery && (
                       <button onClick={() => setSearchQuery('')} className="ml-2 text-gray-500 hover:text-white">
                          <X size={16} />
                       </button>
                    )}
                </div>
            </div>

            <div className="relative z-10 flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center justify-center pt-20">
                        <Loader2 size={32} className="text-[#0BD9E5] animate-spin mb-4" />
                        <p className="text-gray-400 font-medium">Scanning live rooms...</p>
                    </div>
                ) : filteredRooms.length === 0 ? (
                    <div className="flex flex-col items-center justify-center pt-20 text-center opacity-60">
                        <Radio size={48} className="text-gray-600 mb-4" strokeWidth={1.5} />
                        <p className="text-lg font-bold text-white mb-1">No active rooms</p>
                        <p className="text-sm text-gray-400 max-w-[250px]">
                           Be the first to create a custom room and invite others to join!
                        </p>
                    </div>
                ) : (
                    filteredRooms.map((room) => {
                        const isFull = (room.participantUids?.length || 0) >= (room.maxSize || 8);
                        const isEnded = room.status === 'Ended';
                        
                        let timeAgo = '';
                        if (room.createdAt) {
                            try {
                                timeAgo = formatDistanceToNow(room.createdAt.toMillis(), { addSuffix: true });
                            } catch(e) {}
                        }
                        
                        return (
                            <motion.div
                                key={room.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={() => handleRoomClick(room)}
                                className={`bg-[#111] border border-gray-800 hover:border-[#0BD9E5]/30 rounded-2xl p-4 flex flex-col cursor-pointer transition-all ${(isFull || isEnded) ? 'opacity-50' : ''}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-bold text-base text-white truncate flex items-center gap-2">
                                        {room.password && <Lock size={14} className="text-gray-400" />}
                                        <span className="truncate">{room.name}</span>
                                    </h3>
                                    <span className="px-2 py-0.5 bg-gray-900 border border-gray-700 rounded-full text-[10px] font-bold text-gray-400 font-mono tracking-wider shrink-0">
                                        {room.code}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400">
                                        <Users size={14} />
                                        <span>{room.participantUids?.length || 0} / {room.maxSize || 8}</span>
                                        {isEnded ? (
                                            <span className="ml-2 text-red-500 font-bold uppercase">ENDED</span>
                                        ) : isFull ? (
                                            <span className="ml-2 text-red-400">FULL</span>
                                        ) : null}
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <div className="text-[10px] text-gray-500 font-medium">
                                            {room.showCollegeNames ? 'Colleges Visible' : 'Fully Anonymous'}
                                        </div>
                                        {timeAgo && (
                                            <div className="text-[9px] text-gray-600 font-medium mt-0.5">
                                                Created {timeAgo}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })
                )}
            </div>

            <AnimatePresence>
                {needsPassword && (
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                    >
                        <motion.div 
                            initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-[#111] border border-gray-800 p-6 rounded-3xl w-full max-w-sm relative"
                        >
                            <button 
                                onClick={() => { setNeedsPassword(false); setPasswordInput(''); setJoinError(''); }}
                                className="absolute top-4 right-4 text-gray-500 hover:text-white"
                            >
                                <X size={20} />
                            </button>
                            
                            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4 text-[#0BD9E5]">
                                <Lock size={24} />
                            </div>
                            
                            <h3 className="text-xl font-bold mb-2">Private Room</h3>
                            <p className="text-sm text-gray-400 mb-6">Enter the password to join "{selectedRoom?.name}".</p>
                            
                            <input
                                type="password"
                                value={passwordInput}
                                onChange={(e) => { setPasswordInput(e.target.value); setJoinError(''); }}
                                placeholder="Room Password"
                                className="w-full bg-black border border-gray-800 focus:border-[#0BD9E5]/50 rounded-xl px-4 py-3 text-white font-bold tracking-widest text-center mb-2 outline-none"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                            />
                            
                            {joinError && (
                                <p className="text-red-500 text-xs font-semibold flex items-center justify-center gap-1 mt-2 mb-4">
                                    <AlertCircle size={12} /> {joinError}
                                </p>
                            )}

                            <button 
                                onClick={handlePasswordSubmit}
                                disabled={isJoining}
                                className="w-full mt-4 bg-[#0BD9E5] text-black font-bold py-3.5 rounded-xl hover:bg-[#09bcc7] transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
                            >
                                {isJoining ? <Loader2 size={18} className="animate-spin" /> : 'Join Room'}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
